import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { Switch } from "@/components/ui/switch";

interface SortablePageSectionProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  sectionBg: string;
  onBgChange: (value: string) => void;
}

export function SortablePageSection({ id, label, description, checked, onCheckedChange, sectionBg, onBgChange }: SortablePageSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-4 border border-border rounded-lg bg-background"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-medium">{label}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs text-muted-foreground">Background:</span>
            <button
              type="button"
              onClick={() => onBgChange('white')}
              className={`w-6 h-6 rounded-full border-2 transition-all ${
                sectionBg === 'white' 
                  ? 'border-primary ring-2 ring-primary/30' 
                  : 'border-border hover:border-foreground/40'
              }`}
              style={{ backgroundColor: 'white' }}
              title="White background"
            />
            <button
              type="button"
              onClick={() => onBgChange('grey')}
              className={`w-6 h-6 rounded-full border-2 transition-all ${
                sectionBg === 'grey' 
                  ? 'border-primary ring-2 ring-primary/30' 
                  : 'border-border hover:border-foreground/40'
              }`}
              title="Light grey background"
            >
              <span 
                className="block w-full h-full rounded-full" 
                style={{ backgroundColor: 'hsl(210, 17%, 91%)' }}
              />
            </button>
          </div>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}
